import React, { useState } from "react";
import type { ChangeEvent } from "react";
import styles from "./FileUploadBox.module.css";
import fileUploadIcon from "@assets/icons/upload.svg";

interface FileUploadBoxProps {
  label: string;
  accept?: string;
  uploadState: "idle" | "uploading" | "completed" | "error";
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  fileName?: string;
  fileSize?: string;
  onRemove?: () => void;
}

const FileUploadBox = ({
  label,
  accept = ".pdf",
  uploadState,
  onFileChange,
  fileName,
  fileSize,
  onRemove,
}:FileUploadBoxProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Pass dropped files into existing handler
    onFileChange({
      target: { files },
    } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <div className={styles.fileSection}>
      <p className={styles.sectionLabel}>{label}</p>

      <div
        className={`${styles.uploadBox} ${isDragging ? styles.dragActive : ""}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {uploadState === "idle" && (
          <>
            <img
              src={fileUploadIcon}
              alt="File Upload Icon"
              className={styles.uploadIcon}
            />

            <input
              type="file"
              accept={accept}
              className={styles.fileInput}
              id={label}
              onChange={onFileChange}
            />

            <label htmlFor={label} className={styles.uploadPlaceholder}>
              Click to upload PDF <br /> <span>or drag and drop</span>
            </label>
          </>
        )}

        {uploadState === "uploading" && (
          <div className={styles.uploadingState}>
            <div className={styles.fileInfo}>
              <div className={styles.fileIcon}>📄</div>

              <div className={styles.fileDetails}>
                <p className={styles.fileName}>{fileName}</p>
                <p className={styles.fileSize}>{fileSize}</p>
              </div>

              <button
                className={styles.cancelButton}
                onClick={onRemove}
                aria-label="Cancel upload"
              >
                ✕
              </button>
            </div>
              {/* <p className={styles.progressPercent}>{progress}%</p> */}
              <div className={styles.progressBarContainer}>
                {/* <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${progress}%` }}
                  />
                </div> */}
                <p className={styles.uploadingText}>Uploading...</p>
              </div>
          </div>
        )}

        {uploadState === "completed" && (
          <div className={styles.completedState}>
            <div className={styles.fileInfo}>
              <div className={styles.successIcon}>✓</div>

              <div className={styles.fileDetails}>
                <p className={styles.fileName}>{fileName}</p>
                <p className={styles.fileSize}>{fileSize}</p>
              </div>

              <button
                className={styles.removeButton}
                onClick={onRemove}
                aria-label="Remove file"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {uploadState === "error" && (
          <div className={styles.uploadError}>
            <p>Upload Failed. Please re-upload.</p>

            <input
              type="file"
              accept={accept}
              className={styles.fileInput}
              id={`${label}-retry`}
              onChange={onFileChange}
            />

            <label htmlFor={`${label}-retry`} className={styles.retryButton}>
              Re-upload File
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadBox;